import { useCallback, useEffect, useRef, useState } from 'react';
import { ApiError } from '../api/client';

export interface PollResult<T> {
  data: T | undefined;
  error: ApiError | Error | undefined;
  loading: boolean;
  reload: () => Promise<void>;
}

/** 一定間隔で再取得する。タブが非表示の間は止める。intervalMs が 0 以下なら再取得しない。 */
export function usePoll<T>(load: () => Promise<T>, intervalMs: number, deps: unknown[] = []): PollResult<T> {
  const [data, setData] = useState<T>();
  const [error, setError] = useState<ApiError | Error>();
  const [loading, setLoading] = useState(true);
  const loadRef = useRef(load);
  loadRef.current = load;

  const reload = useCallback(async () => {
    try {
      const next = await loadRef.current();
      setData(next);
      setError(undefined);
    } catch (caught) {
      setError(caught instanceof Error ? caught : new Error(String(caught)));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    setLoading(true);
    void reload();
    // 0 以下なら最初の 1 回だけ取得する
    if (intervalMs <= 0) return;
    const timer = setInterval(() => {
      if (document.visibilityState === 'visible') void reload();
    }, intervalMs);
    return () => clearInterval(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [intervalMs, reload, ...deps]);

  return { data, error, loading, reload };
}
